import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import communityAPI from '../api';
import {
  buildPaginationMeta,
  normalizeListResponse
} from '../api/normalizers';

const sanitizeFilters = (filters = {}) => {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return acc;
      }
      acc[key] = value;
      return acc;
    }

    if (value === undefined || value === null || value === '') {
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});
};

const fetchLearningPathsPage = async ({
  filters,
  page,
  pageSize,
  paginated
}) => {
  const params = { ...filters };
  if (paginated) {
    params.page = page;
    if (pageSize) {
      params.page_size = pageSize;
    }
  } else if (pageSize) {
    params.page_size = pageSize;
  }

  const response = await communityAPI.learningPaths.getAll(params);
  const normalized = normalizeListResponse(response.data);
  const meta = buildPaginationMeta({
    response: normalized,
    page: paginated ? page : 1,
    pageSize: paginated ? pageSize : normalized.results.length
  });

  return {
    data: normalized.results,
    meta
  };
};

export const useLearningPaths = (filters = {}, config = {}) => {
  const {
    initialPage = 1,
    pageSize: initialPageSize = 12,
    paginated = true,
    skip = false,
    cache = true
  } = config;

  const [page, setPageState] = useState(initialPage);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  const filtersKey = useMemo(() => JSON.stringify(filters || {}), [filters]);
  const sanitizedFilters = useMemo(
    () => sanitizeFilters(filters),
    [filtersKey]
  );

  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => [
      'community',
      'learning-paths',
      { filters: sanitizedFilters, pageSize, paginated },
      page
    ],
    [sanitizedFilters, pageSize, paginated, page]
  );

  const listQuery = useQuery({
    queryKey,
    enabled: !skip,
    queryFn: () =>
      fetchLearningPathsPage({
        filters: sanitizedFilters,
        page,
        pageSize,
        paginated
      }),
    keepPreviousData: true,
    staleTime: cache ? 30_000 : 0,
    gcTime: cache ? 300_000 : 0,
    onError: () => {
      message.error('Failed to fetch learning paths');
    }
  });

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['community', 'learning-paths'] });
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: (data) => communityAPI.learningPaths.create(data),
    onSuccess: async () => {
      message.success('Learning path created successfully');
      await invalidate();
      setPageState(initialPage);
    },
    onError: () => {
      message.error('Failed to create learning path');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => communityAPI.learningPaths.update(id, data),
    onSuccess: async () => {
      message.success('Learning path updated successfully');
      await invalidate();
    },
    onError: () => {
      message.error('Failed to update learning path');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => communityAPI.learningPaths.delete(id),
    onSuccess: async () => {
      message.success('Learning path deleted successfully');
      await invalidate();
    },
    onError: () => {
      message.error('Failed to delete learning path');
    }
  });

  useEffect(() => {
    setPageState(initialPage);
  }, [initialPage, filtersKey]);

  const setPage = useCallback((value) => {
    setPageState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      return typeof next === 'number' && next > 0 ? next : 1;
    });
  }, []);

  const setPageSize = useCallback(
    (value) => {
      setPageSizeState((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        return typeof next === 'number' && next > 0 ? next : initialPageSize;
      });
      setPageState(initialPage);
    },
    [initialPage, initialPageSize]
  );

  const fetchLearningPaths = useCallback(
    async (options = {}) => {
      if (skip) {
        return [];
      }
      const targetPage = options.page ? Number(options.page) : page;
      if (targetPage !== page) {
        setPage(targetPage);
      }
      const result = await queryClient.fetchQuery({
        queryKey: [
          'community',
          'learning-paths',
          { filters: sanitizedFilters, pageSize, paginated },
          targetPage
        ],
        queryFn: () =>
          fetchLearningPathsPage({
            filters: sanitizedFilters,
            page: targetPage,
            pageSize,
            paginated
          })
      });
      return result?.data ?? [];
    },
    [
      skip,
      queryClient,
      sanitizedFilters,
      pageSize,
      paginated,
      page,
      setPage
    ]
  );

  const meta = listQuery.data?.meta;
  const totalPages = meta?.totalPages ?? 1;

  return {
    learningPaths: listQuery.data?.data ?? [],
    loading: listQuery.isPending,
    error: listQuery.error ?? null,
    fetchLearningPaths,
    createLearningPath: async (data) => {
      const response = await createMutation.mutateAsync(data);
      return response?.data;
    },
    updateLearningPath: async (id, data) => {
      const response = await updateMutation.mutateAsync({ id, data });
      return response?.data;
    },
    deleteLearningPath: (id) => deleteMutation.mutateAsync(id),
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount: meta?.count ?? 0,
    totalPages,
    hasNext: Boolean(meta?.hasNext),
    hasPrevious: Boolean(meta?.hasPrevious)
  };
};

export default useLearningPaths;
